import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  Col,
  ColorPicker,
  Form,
  Input,
  Modal,
  Row,
  Typography,
  message,
} from 'antd';
import {
  DndContext,
  closestCenter,
  useSensors,
  useSensor,
  PointerSensor,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import './style.scss';

import PdButton from 'components/pd-button';

const { confirm } = Modal;
const { Title } = Typography;

export default function ProductColor({ data, dataModifier, imageList }) {
  const [editIdx, setEditIdx] = useState(null);
  const [formLayout] = useState('vertical');
  const [modalData, setModalData] = useState({});
  const [modalImage, setModalImage] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedImage, setSelectedImage] = useState({});

  const formItemLayout =
    formLayout === 'horizontal'
      ? {
          labelCol: { span: 4 },
          wrapperCol: { span: 14 },
        }
      : null;

  function deleteColor(index) {
    const newColor = [...data];
    newColor[index]['_destroy'] = true;
    newColor[index]['status'] = 'deleted';
    dataModifier((prevState) => ({
      ...prevState,
      product_colors_attributes: newColor,
    }));
  }

  function showModal(value = {}, title) {
    const { order_level = null } = value;

    if (title === 'Edit') setEditIdx(order_level);
    setSelectedImage({});
    setModalData(value);
    setModalVisible(true);
    setModalTitle(`${title} Color`);
  }

  function SortableItem({ value = {}, sortIndex }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: value.id || sortIndex,
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.5 : 1,
      userSelect: 'none',
    };

    const {
      _destroy = '',
      color = '',
      hex_code = '',
      total_quantity = 0,
      warehouses = [],
    } = value;

    function checkIsStockZero(sortIndex) {
      const { total_quantity = 0 } = data[sortIndex] || {};
      confirm({
        title: 'Are you sure you want to delete it?',
        onOk() {
          if (!total_quantity) {
            deleteColor(sortIndex);
          } else {
            message.error("Variant can't be deleted");
          }
        },
      });
    }

    if (total_quantity) {
      return (
        <div
          ref={setNodeRef}
          style={{ ...style, display: _destroy ? 'none' : 'block' }}
          className="draggable-item"
          {...attributes}>
          {warehouses.map(
            ({ quantity = 0, warehouse: { title = '' } = {} }, idx) => (
              <Row key={idx} gutter={20} type="flex" align="middle">
                {!idx ? (
                  <>
                    <Col
                      span={2}
                      className="draggable draggable-content color-list"
                      {...listeners}>
                      {hex_code !== null && hex_code !== undefined ? (
                        <div
                          className="color-preview"
                          style={{ backgroundColor: `#${hex_code}` }}></div>
                      ) : (
                        <div className="invisible">invisible</div>
                      )}
                    </Col>
                    <Col
                      span={6}
                      className="draggable draggable-content color-list"
                      {...listeners}>
                      <span className="fs-16">{color}</span>
                    </Col>
                  </>
                ) : (
                  <Col
                    lg={{ span: 7, offset: 1 }}
                    className="draggable draggable-content color-list"
                    {...listeners}></Col>
                )}

                <Col span={6} {...listeners}>{title}</Col>
                <Col className="pd-align-center" lg={3} {...listeners}>
                  {quantity}
                </Col>
              </Row>
            )
          )}
          <Row
            gutter={20}
            className="pd-margin-top-md"
            type="flex"
            justify="center">
            <Col>
              <PdButton
                type="danger"
                className="height-32"
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  checkIsStockZero(sortIndex);
                }}>
                Delete
              </PdButton>
            </Col>
            <Col>
              <PdButton
                type="primary"
                className="height-32"
                onClick={(e) => {
                  e.stopPropagation();
                  showModal(value, 'Edit');
                }}>
                Edit
              </PdButton>
            </Col>
          </Row>
        </div>
      );
    } else {
      return (
        <div
          ref={setNodeRef}
          style={{ ...style, display: _destroy ? 'none' : 'block' }}
          className="draggable-item"
          {...attributes}>
          <Row gutter={20}>
            <Col span={2} className="draggable draggable-content color-list" {...listeners}>
              {hex_code !== null && hex_code !== undefined ? (
                <div
                  className="color-preview"
                  style={{ backgroundColor: `#${hex_code}` }}></div>
              ) : (
                <div className="invisible">invisible</div>
              )}
            </Col>
            <Col span={17} className="draggable draggable-content color-list" {...listeners}>
              <span className="fs-16">{color}</span>
            </Col>
            <Col span={5} className="draggable draggable-button color-list">
              <PdButton
                type="danger"
                className="height-32"
                outline
                onClick={(e) => {
                  e.stopPropagation();
                  checkIsStockZero(sortIndex);
                }}>
                Delete
              </PdButton>
              <PdButton
                type="primary"
                className="height-32"
                onClick={(e) => {
                  e.stopPropagation();
                  showModal(value, 'Edit');
                }}>
                Edit
              </PdButton>
            </Col>
          </Row>
        </div>
      );
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    })
  );

  function handleDragEnd(event) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      const oldIndex = data.findIndex((item, index) => (item.id || index) === active.id);
      const newIndex = data.findIndex((item, index) => (item.id || index) === over.id);

      let list = [...data];
      let temp = list[oldIndex];
      list.splice(oldIndex, 1);
      list.splice(newIndex, 0, temp);
      list.forEach((item, index) => {
        list[index] = {
          ...item,
          order_level: index,
        };
      });
      dataModifier((prevState) => ({
        ...prevState,
        product_colors_attributes: list,
      }));
    }
  }

  function ProductColorSortableList({ items }) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}>
        <SortableContext
          items={items.map((item, index) => ({ id: item.id || index }))}
          strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableItem
              key={item.id || index}
              value={item}
              sortIndex={index}
            />
          ))}
        </SortableContext>
      </DndContext>
    );
  }

  function onOk() {
    let list = [...data];
    const { color: modalDataColor, id: modalDataId } = modalData;
    if (modalDataColor) {
      if (modalDataId || editIdx !== null) {
        const newValue = list.map((data, idx) => {
          if (idx === editIdx) {
            return modalData;
          } else {
            return data;
          }
        });

        dataModifier((prevState) => ({
          ...prevState,
          product_colors_attributes: newValue,
        }));
        setEditIdx(null);
      } else {
        list.unshift(modalData);
        list.forEach((item, index) => {
          list[index] = {
            ...item,
            order_level: index,
          };
        });
        dataModifier((prevState) => ({
          ...prevState,
          product_colors_attributes: list,
        }));
      }
      setModalVisible(false);
      setModalData({});
    } else {
      message.error("Color title can't be empty");
    }
  }
  function onCancel() {
    setModalData({});
    setSelectedImage({});
    setModalVisible(false);
  }

  function onChangeColorPick(color) {
    const hexColor = color.toHexString();
    const finalColor = hexColor.split('#')[1];
    setModalData((prevState) => ({ ...prevState, hex_code: finalColor }));
  }

  function onChangeModal(e) {
    const value = e.target.value;
    setModalData((prevState) => ({ ...prevState, color: value }));
  }

  function onChangeSku({ target: { value = '' } } = {}) {
    setModalData((prev) => ({ ...prev, sku: value }));
  }

  function checkImage(val) {
    setModalData((prevState) => ({
      ...prevState,
      gallery_id: val.id,
      product_gallery_id: val.id,
    }));
    setModalImage(false);
  }

  useEffect(() => {
    const images = imageList.find((x) => x.id === modalData.gallery_id);
    if (images) {
      setSelectedImage(images);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalData['gallery_id']]);

  return (
    <div className="section-container product-color">
      <div className="inner-section">
        <Title className="pageSubtitle" level={3}>
          Color
        </Title>
        <div className="buttonSection">
          <PdButton
            type="primary"
            full
            outline
            onClick={() => showModal({}, 'Add')}>
            Add Color
          </PdButton>
        </div>
        <div id="drag-content-color">
          {data.filter((x) => !x._destroy).length === 0 ? (
            <div className="no-color">No Color Variants added yet</div>
          ) : (
            <>
              <Row className="pd-margin-top-lg pd-margin-bottom-lg">
                <Col lg={{ span: 7, offset: 1 }}> Variant</Col>
                <Col span={7}> Warehouse</Col>
                <Col className="pd-align-center" span={1}>
                  Stock
                </Col>
              </Row>
              <ProductColorSortableList items={data} />
            </>
          )}
        </div>
      </div>
      <Modal
        title={modalTitle}
        open={modalVisible}
        onCancel={onCancel}
        centered
        className="modal-color"
        onOk={onOk}>
        <Row>
          <Col span={24}>
            <Form.Item label="Title" {...formItemLayout}>
              <Input
                placeholder="Enter color title"
                value={modalData.color}
                onChange={onChangeModal}
              />
            </Form.Item>
            <Form.Item label="SKU" {...formItemLayout}>
              <Input
                placeholder="Enter color's sku"
                value={modalData.sku}
                onChange={onChangeSku}
              />
            </Form.Item>
            <Form.Item label="Color (optional)" {...formItemLayout}>
              <ColorPicker
                onChange={onChangeColorPick}
                value={
                  modalData.hex_code !== null &&
                  modalData.hex_code !== undefined
                    ? `#${modalData.hex_code}`
                    : '#ffffff'
                }
                showText
              />
            </Form.Item>
            <Form.Item label="Image (optional)" {...formItemLayout}>
              <div
                className="container-image"
                onClick={() => setModalImage(true)}>
                {!selectedImage._destroy && selectedImage.secure_url ? (
                  <img
                    className="image-modal"
                    src={selectedImage.secure_url}
                    alt="product"
                  />
                ) : (
                  <div className="image-modal">select image</div>
                )}
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Modal>
      <Modal
        title="Add Image"
        className="modal--imageList"
        open={modalImage}
        centered
        onOk={() => setModalImage(false)}
        onCancel={() => setModalImage(false)}>
        <div className="modal-container">
          <Row>
            <Col span={24} className="image-catalog">
              {imageList.map((image, i) => (
                <Row
                  key={i}
                  className="image-item"
                  style={{ display: (image._destroy || !image.id) && 'none' }}>
                  <Col span={1}>
                    <Checkbox
                      checked={selectedImage.id === image.id}
                      onChange={() => checkImage(image)}
                    />
                  </Col>
                  <Col span={8}>
                    <img
                      className="picture"
                      src={image.secure_url}
                      alt="catalog product"
                    />
                  </Col>
                  <Col span={15} className="image-title">
                    <span className=" bold">{image.title}</span>
                  </Col>
                </Row>
              ))}
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
}
